import type {
  InteractiveSurveyAuthState,
  MapFilters,
  MapKey,
} from "@/features/auth/types";
import type { Property, Survey, SurveyGroup } from "@/features/survey/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const getShortlistSession = () => {
  if (typeof window !== "undefined") {
    return JSON.parse(window.sessionStorage.getItem("shortlisted") || "false");
  }
  return false;
};
const initialState: InteractiveSurveyAuthState = {
  isAuthenticated: false,
  isLoading: false,
  error: null,
  interactiveToken: null,
  surveyId: null,
  surveyGroupId: null,
  currentSurvey: null,
  currentSurveyGroup: null,
  groupSurveys: null,
  mapFilters: {
    showShortlistOnly: getShortlistSession(),
    showHidden: false,
    shortListTogglePDFStatus: false,
  },
  marketMapKeys: [],
  hiddenMapKeyColors: [],
};

const interactiveSurveySlice = createSlice({
  name: "interactiveSurvey",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setAuthenticated: (
      state,
      action: PayloadAction<{ token: string; surveyId: string }>,
    ) => {
      state.isAuthenticated = true;
      state.interactiveToken = action.payload.token;
      state.surveyId = action.payload.surveyId;
      state.isLoading = false;
      state.error = null;
    },
    setGroupAuthenticated: (
      state,
      action: PayloadAction<{ token: string; surveyGroupId: string }>,
    ) => {
      state.isAuthenticated = true;
      state.interactiveToken = action.payload.token;
      state.surveyGroupId = action.payload.surveyGroupId;
      state.isLoading = false;
      state.error = null;
    },
    setCurrentSurvey: (state, action: PayloadAction<Survey>) => {
      state.currentSurvey = action.payload;
    },
    setCurrentSurveyGroup: (
      state,
      action: PayloadAction<{ surveyGroup: SurveyGroup; surveys: Survey[] }>,
    ) => {
      state.currentSurveyGroup = action.payload.surveyGroup;
      state.groupSurveys = action.payload.surveys;
    },
    updateProperty: (state, action: PayloadAction<Property>) => {
      if (state.currentSurvey?.properties) {
        const propertyIndex = state.currentSurvey.properties.findIndex(
          (p) => p.id === action.payload.id,
        );
        if (propertyIndex !== -1) {
          state.currentSurvey.properties[propertyIndex] = action.payload;
        }
      }
      // Keep the group-level copy in sync so group views (e.g. Shortlist) stay current
      const groupSurvey = state.groupSurveys?.find(
        (survey) => survey.id === state.currentSurvey?.id,
      );
      const groupPropertyIndex =
        groupSurvey?.properties?.findIndex(
          (p) => p.id === action.payload.id,
        ) ?? -1;
      if (groupSurvey?.properties && groupPropertyIndex !== -1) {
        groupSurvey.properties[groupPropertyIndex] = action.payload;
      }
    },
    clearAuthentication: (state) => {
      Object.assign(state, initialState);
    },
    restoreAuthentication: (
      state,
      action: PayloadAction<{ token: string; surveyId: string }>,
    ) => {
      state.isAuthenticated = true;
      state.interactiveToken = action.payload.token;
      state.surveyId = action.payload.surveyId;
    },
    updateMapFilters: (state, action: PayloadAction<Partial<MapFilters>>) => {
      state.mapFilters = { ...state.mapFilters, ...action.payload };
    },
    resetMapFilters: (state) => {
      state.mapFilters = {
        showShortlistOnly: false,
        showHidden: false,
        shortListTogglePDFStatus: false,
      };
    },
    setShortListTogglePDFStatus: (state, action: PayloadAction<boolean>) => {
      state.mapFilters.shortListTogglePDFStatus = action.payload;
    },
    setMarketMapKeys: (state, action: PayloadAction<MapKey[]>) => {
      state.marketMapKeys = action.payload;
    },
    addMarketMapKeys: (state, action: PayloadAction<MapKey>) => {
      state.marketMapKeys.push(action.payload);
    },
    deleteMarketMapKey: (state, action: PayloadAction<string>) => {
      state.marketMapKeys = state.marketMapKeys.filter(
        (item) => item.mapKeyId !== action.payload,
      );
    },
    updateToggleLabelMapKey: (state, action: PayloadAction<MapKey>) => {
      const index = state.marketMapKeys.findIndex(
        (item) => item.mapKeyId === action.payload.mapKeyId,
      );
      if (index !== -1) {
        state.marketMapKeys[index] = action.payload;
      }
    },
    setHiddenMapKeyColors: (state, action: PayloadAction<string[]>) => {
      state.hiddenMapKeyColors = action.payload;
    },
    toggleMapKeyVisibility: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (state.hiddenMapKeyColors.includes(key)) {
        state.hiddenMapKeyColors = state.hiddenMapKeyColors.filter(
          (k) => k !== key,
        );
      } else {
        state.hiddenMapKeyColors.push(key);
      }
    },
  },
});

export const {
  setLoading,
  setError,
  setAuthenticated,
  setGroupAuthenticated,
  setCurrentSurvey,
  setCurrentSurveyGroup,
  updateProperty,
  clearAuthentication,
  restoreAuthentication,
  updateMapFilters,
  resetMapFilters,
  setShortListTogglePDFStatus,
  setMarketMapKeys,
  addMarketMapKeys,
  deleteMarketMapKey,
  updateToggleLabelMapKey,
  setHiddenMapKeyColors,
  toggleMapKeyVisibility,
} = interactiveSurveySlice.actions;

export default interactiveSurveySlice.reducer;
